import React, { useCallback, useMemo, useState } from 'react';
import { Download, File, FileText, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../context';
import { apiGetDownloadUrl, apiGetPreviewUrl } from '../api';
import FileViewer from './FileViewer/FileViewer';

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif']);

const previewTypeOf = (file) => {
  const contentType = String(file.contentType || file.mimeType || '').toLowerCase();
  const extension = String(file.name || '').split('.').pop().toLowerCase();
  if (contentType === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (contentType.startsWith('image/') || IMAGE_EXTENSIONS.has(extension)) return 'image';
  return null;
};

const legacyDriveId = (file) => {
  if (file.id) return file.id;
  const match = String(file.webViewLink || '').match(/\/d\/([^/]+)/);
  return match?.[1] || '';
};

const clickDownloadUrl = (url, name, openInNewTab = false) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name || '';
  if (openInNewTab) {
    anchor.target = '_blank';
    anchor.rel = 'noreferrer';
  }
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

export default function FileLink({ file, className = '', style, iconSize = 14, children }) {
  const { jwtToken } = useAuth();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const label = children ?? file.name;
  const previewType = useMemo(() => previewTypeOf(file), [file]);
  const driveId = legacyDriveId(file);

  const download = useCallback(async (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    setError('');
    try {
      if (file.storageKey) {
        const { url } = await apiGetDownloadUrl(jwtToken, file.storageKey);
        clickDownloadUrl(url, file.name);
      } else {
        const url = driveId
          ? `https://drive.google.com/uc?export=download&id=${encodeURIComponent(driveId)}`
          : file.webViewLink;
        if (!url) throw new Error('Download link is unavailable');
        clickDownloadUrl(url, file.name, true);
      }
    } catch (err) {
      setError(err.message || 'Could not download file');
    } finally {
      setDownloading(false);
    }
  }, [downloading, driveId, file, jwtToken]);

  const open = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!previewType) {
      download(event);
      return;
    }

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl('');
    setError('');
    try {
      if (file.storageKey) {
        const { url } = await apiGetPreviewUrl(jwtToken, file.storageKey);
        setPreviewUrl(url);
      } else {
        const url = driveId
          ? `https://drive.google.com/file/d/${encodeURIComponent(driveId)}/preview`
          : file.webViewLink;
        if (!url) throw new Error('Preview link is unavailable');
        setPreviewUrl(url);
      }
    } catch (err) {
      setError(err.message || 'Could not preview file');
    } finally {
      setPreviewLoading(false);
    }
  };

  const FileIcon = previewType === 'pdf' ? FileText : previewType === 'image' ? ImageIcon : File;

  return (
    <>
      <span
        className={`file-link-shell ${className}`.trim()}
        style={style}
        title={error || (previewType ? 'Preview file' : 'Download file')}
      >
        <span
          role="button"
          tabIndex={0}
          className="file-link-main"
          onClick={open}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              open(event);
            }
          }}
        >
          <FileIcon size={iconSize} />
          <span className="file-link-label">{previewLoading ? 'Opening…' : label}</span>
        </span>
        <button
          type="button"
          className="file-link-download"
          onClick={download}
          disabled={downloading}
          aria-label={`Download ${file.name || 'file'}`}
          title="Download"
        >
          <Download size={13} />
        </button>
      </span>

      {previewOpen && (
        <FileViewer
          file={file}
          type={previewType}
          url={previewUrl}
          loading={previewLoading}
          error={error}
          downloading={downloading}
          onDownload={download}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}
